import clsx from "clsx";
import { ClickAwayListener, Popper } from "@mui/material";
import {
    Children,
    cloneElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactElement,
    type ReactNode,
} from "react";
import { twMerge } from "tailwind-merge";
import { SelectContext, type MenuItemProps } from "./MenuItem";

export interface SelectProps {
    value: unknown;
    onChange: (event: { target: { value: unknown } }) => void;
    children: ReactNode;
    className?: string;
    displayEmpty?: boolean;
    open?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    slotProps?: {
        input?: {
            className?: string;
            "aria-label"?: string;
        };
    };
    IconComponent?: ReactElement | (() => ReactElement);
    disabled?: boolean;
    "data-testid"?: string;
    readOnly?: boolean;
}

function useControlledOpen(controlledOpen: boolean | undefined, onOpen?: () => void, onClose?: () => void) {
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : internalOpen;

    const setOpen = useCallback(
        (next: boolean) => {
            if (!isControlled) {
                setInternalOpen(next);
            }
            if (next) {
                onOpen?.();
            } else {
                onClose?.();
            }
        },
        [isControlled, onOpen, onClose],
    );

    return { open, setOpen };
}

export function Select({
    value,
    onChange,
    children,
    className,
    displayEmpty,
    open: controlledOpen,
    onOpen,
    onClose,
    slotProps,
    IconComponent,
    disabled,
    "data-testid": dataTestId,
    readOnly,
}: SelectProps) {
    const { open, setOpen } = useControlledOpen(controlledOpen, onOpen, onClose);
    const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
    const [triggerWidth, setTriggerWidth] = useState<number | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);

    const items = useMemo(
        () => Children.toArray(children).filter(Boolean) as ReactElement<MenuItemProps>[],
        [children],
    );

    const selectedItem = useMemo(() => items.find((child) => child?.props?.value === value), [items, value]);

    const toggleOpen = useCallback(() => {
        if (disabled || readOnly) return;
        setOpen(!open);
    }, [disabled, readOnly, setOpen, open]);

    const close = useCallback(() => setOpen(false), [setOpen]);

    const handleSelect = useCallback(
        (nextValue: unknown) => {
            onChange({ target: { value: nextValue } });
        },
        [onChange],
    );

    const displayContent = useMemo(() => {
        if (selectedItem) return selectedItem.props.children;
        if (displayEmpty) return null;
        return null;
    }, [selectedItem, displayEmpty]);

    useEffect(() => {
        if (triggerRef.current) {
            setTriggerWidth(triggerRef.current.getBoundingClientRect().width);
        }
    }, [open, className, slotProps?.input?.className, value]);

    useEffect(() => {
        setPortalContainer(document.getElementById("app-theme-root"));
    }, []);

    const triggerClassName = useMemo(
        () =>
            twMerge(
                clsx(
                    "relative inline-flex w-full items-center justify-between gap-2 rounded-[6px] py-2 text-[15px] leading-[22.5px] text-primary",
                    "outline-none ring-0",
                    "disabled:cursor-not-allowed disabled:opacity-60",
                    "read-only:cursor-default read-only:opacity-80",
                ),
                className,
            ),
        [className],
    );

    const inputClassName = useMemo(
        () => twMerge("flex-1 text-left select-none", slotProps?.input?.className),
        [slotProps?.input?.className],
    );

    const rotatedIconElement = useMemo(() => {
        const iconElement = typeof IconComponent === "function" ? IconComponent() : IconComponent;
        if (!iconElement) return null;
        return cloneElement(iconElement, {
            className: clsx(iconElement.props.className, open && "transition-all -rotate-180"),
        });
    }, [IconComponent, open]);

    const contextValue = useMemo(
        () => ({ value, onSelect: handleSelect, close, disabled }),
        [value, handleSelect, close, disabled],
    );

    return (
        <div className="relative inline-flex w-full z-auto" data-testid={dataTestId}>
            <button
                ref={triggerRef}
                type="button"
                className={triggerClassName}
                onClick={toggleOpen}
                aria-expanded={open}
                aria-haspopup="listbox"
                aria-label={slotProps?.input?.["aria-label"]}
                disabled={disabled}
                data-testid={dataTestId ? `${dataTestId}-button` : undefined}
            >
                <span className={inputClassName}>{displayContent}</span>
                {!readOnly && rotatedIconElement}
            </button>
            <Popper
                open={open}
                anchorEl={triggerRef.current}
                placement="right-start"
                container={portalContainer}
                style={{ width: triggerWidth ?? undefined, zIndex: 5000 }}
            >
                <ClickAwayListener onClickAway={close}>
                    <div
                        role="listbox"
                        className="max-h-72 w-full overflow-visible rounded-[6px] card-bg shadow-lg border-primary/20 border-[1px] z-[5000]"
                    >
                        <SelectContext.Provider value={contextValue}>
                            <div className="flex flex-col py-1">{children}</div>
                        </SelectContext.Provider>
                    </div>
                </ClickAwayListener>
            </Popper>
        </div>
    );
}

Select.displayName = "Select";
